import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    type: 'custom',
    description: '',
    config: { model: 'gpt-4', provider: 'openai', temperature: 0.7 }
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get('/api/agents');
      setAgents(res.data.agents);
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/agents', newAgent);
      toast.success('Agent created');
      setShowCreate(false);
      setNewAgent({
        name: '',
        type: 'custom',
        description: '',
        config: { model: 'gpt-4', provider: 'openai', temperature: 0.7 }
      });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create agent');
    }
  };

  const toggleAgent = async (id) => {
    try {
      await axios.post(`/api/agents/${id}/toggle`);
      fetchAgents();
    } catch (error) {
      toast.error('Failed to toggle agent');
    }
  };

  const deleteAgent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    
    try {
      await axios.delete(`/api/agents/${id}`);
      toast.success('Agent deleted');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Agent Workforce</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors"
        >
          + Spawn Agent
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <AgentCard
            key={agent._id}
            agent={agent}
            onToggle={() => toggleAgent(agent._id)}
            onDelete={() => deleteAgent(agent._id)}
          />
        ))}
      </div>

      {/* Create Agent Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Spawn New Agent</h3>
            <form onSubmit={createAgent} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  placeholder="e.g., MarketingBot"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={newAgent.type}
                  onChange={(e) => setNewAgent({ ...newAgent, type: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                >
                  <option value="custom">Custom</option>
                  <option value="coder">Developer</option>
                  <option value="researcher">Researcher</option>
                  <option value="writer">Writer</option>
                  <option value="analyst">Analyst</option>
                  <option value="designer">Designer</option>
                  <option value="budget">Budget Optimizer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={newAgent.description}
                  onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  rows="2"
                  placeholder="What does this agent do?"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">AI Provider</label>
                <select
                  value={newAgent.config.provider}
                  onChange={(e) => setNewAgent({ ...newAgent, config: { ...newAgent.config, provider: e.target.value } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="cohere">Cohere</option>
                  <option value="local">Local (Ollama)</option>
                  <option value="runpod">RunPod</option>
                  <option value="auto">Auto (Budget-Optimized)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Model</label>
                <input
                  type="text"
                  value={newAgent.config.model}
                  onChange={(e) => setNewAgent({ ...newAgent, config: { ...newAgent.config, model: e.target.value } })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  placeholder="gpt-4"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Temperature ({newAgent.config.temperature})</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={newAgent.config.temperature}
                  onChange={(e) => setNewAgent({ ...newAgent, config: { ...newAgent.config, temperature: parseFloat(e.target.value) } })}
                  className="w-full"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors"
                >
                  Spawn Agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const AgentCard = ({ agent, onToggle, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 hover:border-indigo-500/30 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${getAgentColor(agent.type)}`}>
            {agent.avatar || getDefaultAvatar(agent.type)}
          </div>
          <div>
            <h3 className="font-semibold text-lg">{agent.name}</h3>
            <p className="text-sm text-gray-500 capitalize">{agent.type} Agent</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${getStatusColor(agent.status)}`} />
        </div>
      </div>

      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {agent.description || 'No description provided'}
      </p>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-xl font-bold text-indigo-400">{agent.currentTasks?.length || 0}</div>
          <div className="text-xs text-gray-500">Tasks</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-indigo-400">{Math.round(agent.load || 0)}%</div>
          <div className="text-xs text-gray-500">Load</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-indigo-400">{agent.performance?.tasksCompleted || 0}</div>
          <div className="text-xs text-gray-500">Done</div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onToggle}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            agent.status === 'online'
              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
              : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
          }`}
        >
          {agent.status === 'online' ? 'Online' : 'Offline'}
        </button>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="px-3 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] rounded-lg text-sm transition-colors"
        >
          Details
        </button>
        {agent.type !== 'orchestrator' && (
          <button
            onClick={onDelete}
            className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors"
          >
            Delete
          </button>
        )}
      </div>

      {showDetails && (
        <div className="mt-4 pt-4 border-t border-[#2d2d3d] space-y-2">
          <div className="text-sm">
            <span className="text-gray-500">Provider:</span>{' '}
            <span className="capitalize">{agent.config?.provider}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Model:</span>{' '}
            <span>{agent.config?.model}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Temperature:</span>{' '}
            <span>{agent.config?.temperature}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Max Tasks:</span>{' '}
            <span>{agent.maxConcurrentTasks}</span>
          </div>
          {agent.capabilities?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {agent.capabilities.map((cap, i) => (
                <span key={i} className="px-2 py-1 bg-[#0a0a0f] rounded text-xs text-gray-400">
                  {cap.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getAgentColor = (type) => {
  const colors = {
    orchestrator: 'bg-gradient-to-br from-indigo-500 to-purple-600',
    coder: 'bg-gradient-to-br from-blue-500 to-cyan-500',
    researcher: 'bg-gradient-to-br from-emerald-500 to-teal-500',
    writer: 'bg-gradient-to-br from-amber-500 to-orange-500',
    analyst: 'bg-gradient-to-br from-red-500 to-pink-500',
    designer: 'bg-gradient-to-br from-pink-500 to-rose-500',
    budget: 'bg-gradient-to-br from-green-500 to-emerald-600',
    custom: 'bg-gradient-to-br from-gray-600 to-gray-500'
  };
  return colors[type] || colors.custom;
};

const getDefaultAvatar = (type) => {
  const avatars = {
    orchestrator: '🎯',
    coder: '👨‍💻',
    researcher: '🔬',
    writer: '✍️',
    analyst: '📊',
    designer: '🎨',
    budget: '💰',
    custom: '🤖'
  };
  return avatars[type] || '🤖';
};

const getStatusColor = (status) => {
  const colors = {
    online: 'bg-green-500',
    busy: 'bg-yellow-500',
    offline: 'bg-gray-500',
    error: 'bg-red-500'
  };
  return colors[status] || 'bg-gray-500';
};

export default Agents;
